import {
  Component,
  computed,
  DestroyRef,
  inject,
  output,
  type Signal,
  ViewEncapsulation,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';

import { CompanySwitchStrategy } from '@core/http-legacy';
import { CompanyVerificationDataService } from '@core/tellent-company-verification-logic';
import { TellentCurrentUserSelectors } from '@core/tellent-data';
import { TellentNavigationRoutingService } from '@core/tellent-logic';
import {
  provideTellentProfileDropdownConfig,
  TellentAdoptedStylesheetsDirective,
  TellentThemeDirective,
  TellentProfileDropdownComponent,
  TELLENT_PROFILE_DIALOG_ID,
} from '@core/tellent-smart-ui';
import type {
  EventEmitterLike,
  TellentCurrentUser,
  TellentWebComponent,
} from '@core/tellent-types';
import { APP_GUID, UserProfileTab } from '@core/tellent-types';

class ProfileAfterCloseNotifier extends Subject<any> {}

@Component({
  selector: 'x-profile-dropdown',
  template: `<tla-profile-dropdown /> `,
  styleUrls: ['../../../styles.less', './profile-dropdown-wrap.component.less'],
  imports: [TellentProfileDropdownComponent],
  encapsulation: ViewEncapsulation.ShadowDom,
  hostDirectives: [TellentThemeDirective, TellentAdoptedStylesheetsDirective],
  changeDetection: ChangeDetectionStrategy.Eager,
  viewProviders: [
    ProfileAfterCloseNotifier,

    provideTellentProfileDropdownConfig(() => {
      const appGuid = inject(APP_GUID);
      const store = inject(Store);
      const companyVerificationDataService = inject(CompanyVerificationDataService);
      const currentUser = <Signal<TellentCurrentUser>>(
        store.selectSignal(TellentCurrentUserSelectors.data)
      );

      const switchStrategy = inject(CompanySwitchStrategy);

      const tellentRouting = inject(TellentNavigationRoutingService);

      const currentMember = computed(() => {
        const user = currentUser();
        return user?.members.find(m => m.seats.some(s => s.app.guid === appGuid));
      });

      const currentOrg = computed(() => {
        const user = currentUser();
        return user.members.find(m => m.org.guid === user.currentOrgGuid)?.org;
      });

      const currentSeat = computed(() => currentMember()?.seats.find(s => s.app.guid === appGuid)!);
      const companies = computed(() => currentUser()?.members);

      const companyVerificationStatus = toSignal(companyVerificationDataService.status$);

      return {
        companies,
        currentUser,
        currentMember,
        currentSeat,
        currentOrg,
        companyVerificationStatus,
        handlers: {
          switchCompany: orgGuid => switchStrategy.switchCompany(orgGuid),
          editProfile: (analyticsContext?: string) =>
            tellentRouting.navigateById({
              id: TELLENT_PROFILE_DIALOG_ID,
              params: {
                tab: UserProfileTab.Profile,
                analyticsContext: 'admin_dropdown',
              },
            }),
          manageCompanies: (analyticsContext?: string) =>
            tellentRouting.navigateById({
              id: TELLENT_PROFILE_DIALOG_ID,
              params: {
                tab: UserProfileTab.Companies,
                analyticsContext: 'admin_dropdown',
              },
            }),
          openPushNotificationSettings: () => {
            // Opens the Manage (host app) push settings side-modal, which is
            // driven by its URL modal convention (`modal-<id>=open`).
            const url = new URL(tellentRouting.location() || '/', window.location.origin);
            url.searchParams.set('modal-push-notifications', 'open');
            tellentRouting.navigate(`${url.pathname}${url.search}`);
          },
        },
      };
    }),
  ],
})
export class TellentProfileDropdownWrapComponent {
  #profileCloseNotifier = inject(ProfileAfterCloseNotifier);
  #destroyRef = inject(DestroyRef);

  public afterProfileDialogClosed: EventEmitterLike<any> = output<any>();

  public ngOnInit() {
    this.#profileCloseNotifier
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(e => this.afterProfileDialogClosed.emit(e));
  }
}

export type TellentProfileDropdownWebComponent = TellentWebComponent<
  TellentProfileDropdownWrapComponent,
  'afterProfileDialogClosed'
>;
